// src/pages/hiring.js
import React, { useState } from "react";
import { Link } from "react-router-dom";

const Section = ({ number, title, children }) => (
  <div style={styles.section}>
    <div style={styles.sectionHeader}>
      <span style={styles.sectionNumber}>{number}</span>
      <h2 style={styles.sectionTitle}>{title}</h2>
    </div>
    <div style={styles.sectionBody}>{children}</div>
  </div>
);

const SubSection = ({ title, children }) => (
  <div style={styles.subSection}>
    <h3 style={styles.subTitle}>{title}</h3>
    {children}
  </div>
);

const BulletList = ({ items }) => (
  <ul style={styles.list}>
    {items.map((item, i) => (
      <li key={i} style={styles.listItem}>
        <span style={styles.bullet}>—</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Tag = ({ children }) => (
  <span style={styles.tag}>{children}</span>
);

export default function Hiring() {
  const [openRole, setOpenRole] = useState(null);

  return (
    <div style={styles.page}>
      <style>{`
        * { box-sizing: border-box; }

        .hiring-card {
          transition: border-color 0.25s ease;
        }
        .hiring-card:hover {
          border-color: rgba(123,25,188,0.4) !important;
        }
        .role-toggle {
          cursor: pointer;
          transition: background 0.2s ease;
        }
        .role-toggle:hover {
          background: rgba(123,25,188,0.08) !important;
        }

        .pdf-btn-link {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 15px 24px;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 600;
          text-decoration: none;
          cursor: pointer;
          transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
          font-family: elza, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          text-align: center;
        }
        .pdf-btn-link:hover {
          transform: translateY(-2px);
          filter: brightness(1.12);
        }
        .pdf-btn-link:active {
          transform: translateY(0px);
        }
        .pdf-btn-purple {
          background: linear-gradient(135deg, #7b19bc 0%, #9f3ee5 100%);
          color: #fff;
          box-shadow: 0 4px 20px rgba(123,25,188,0.45);
        }
        .pdf-btn-purple:hover {
          box-shadow: 0 6px 28px rgba(123,25,188,0.65);
        }
        .pdf-btn-blue {
          background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
          color: #fff;
          box-shadow: 0 4px 20px rgba(37,99,235,0.45);
        }
        .pdf-btn-blue:hover {
          box-shadow: 0 6px 28px rgba(37,99,235,0.65);
        }

        @media (max-width: 768px) {
          .hiring-hero { padding: 100px 20px 60px !important; }
          .hiring-hero h1 { font-size: 32px !important; }
          .hiring-inner { padding: 40px 20px !important; }
          .roles-grid { grid-template-columns: 1fr !important; }
          .warning-grid { grid-template-columns: 1fr !important; }
          .outcome-grid { grid-template-columns: 1fr !important; }
          .pdf-btn-row { flex-direction: column !important; }
        }
        @media (max-width: 480px) {
          .hiring-hero h1 { font-size: 26px !important; }
          .hiring-inner { padding: 30px 16px !important; }
        }
      `}</style>

      {/* ── HERO ── */}
      <div style={styles.hero} className="hiring-hero">
        <p style={styles.heroLabel}>Now Hiring · Immediate Start</p>
        <h1 style={styles.heroTitle}>Company Policy Document</h1>
        <p style={styles.heroSub}>
          Technical Assistant &nbsp;·&nbsp; Sales Development Representative
        </p>
        <div style={styles.heroPills}>
          {["Remote", "Performance-Based", "Immediate Start", "Growth Opportunity"].map((t) => (
            <span key={t} style={styles.pill}>{t}</span>
          ))}
        </div>
      </div>

      {/* ── ROLE CARDS ── */}
      <div style={styles.inner} className="hiring-inner">
        <div style={{ ...styles.rolesGrid }} className="roles-grid">

          {/* SDR Card */}
          <div
            style={{
              ...styles.roleCard,
              borderColor: openRole === "sdr" ? "#7b19bc" : "rgba(255,255,255,0.08)",
            }}
            className="hiring-card role-toggle"
            onClick={() => setOpenRole(openRole === "sdr" ? null : "sdr")}
          >
            <div style={styles.roleCardTop}>
              <div>
                <p style={styles.roleCardLabel}>Role 01</p>
                <h3 style={styles.roleCardTitle}>Sales Development Representative</h3>
                <p style={styles.roleCardSub}>Lead gen · Cold calling · Appointment setting</p>
              </div>
              <span style={styles.chevron}>{openRole === "sdr" ? "▲" : "▼"}</span>
            </div>
            <div style={styles.roleTagRow}>
              <Tag>Performance-Based</Tag>
              <Tag>30-Day Probation</Tag>
              <Tag>Remote</Tag>
            </div>
          </div>

          {/* Technical Card */}
          <div
            style={{
              ...styles.roleCard,
              borderColor: openRole === "tech" ? "#7b19bc" : "rgba(255,255,255,0.08)",
            }}
            className="hiring-card role-toggle"
            onClick={() => setOpenRole(openRole === "tech" ? null : "tech")}
          >
            <div style={styles.roleCardTop}>
              <div>
                <p style={styles.roleCardLabel}>Role 02</p>
                <h3 style={styles.roleCardTitle}>Technical Operations Assistant</h3>
                <p style={styles.roleCardSub}>Tools · CRM · Execution · Daily ops</p>
              </div>
              <span style={styles.chevron}>{openRole === "tech" ? "▲" : "▼"}</span>
            </div>
            <div style={styles.roleTagRow}>
              <Tag>No Coding Required</Tag>
              <Tag>11AM – 4PM</Tag>
              <Tag>Works with Founder</Tag>
            </div>
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div style={styles.divider} />

        {/* ══════════════════════════════════════
            SECTION 1 — GENERAL POLICIES
        ══════════════════════════════════════ */}
        <Section number="01" title="General Company Policies — All Employees">
          <p style={styles.introText}>
            These rules apply to every employee regardless of role. All team members are expected
            to read, understand, and follow them from day one.
          </p>

          <SubSection title="1.1  Attendance & Punctuality">
            <BulletList items={[
              "Report on time according to assigned working hours.",
              "Repeated lateness will be recorded and may result in disciplinary action.",
              "Absence must be communicated at least 24 hours in advance, except in emergencies.",
            ]} />
          </SubSection>

          <SubSection title="1.2  Leave Policy">
            <BulletList items={[
              "Employees are allowed 1 day off per month as standard leave.",
              "Any additional leave requires prior approval from management.",
              "Unapproved absence may lead to disciplinary action.",
            ]} />
          </SubSection>

          <SubSection title="1.3  Disciplinary System — 3-Warning Policy">
            <div style={{ ...styles.warningGrid }} className="warning-grid">
              {[
                { n: "1st", label: "Verbal or Written Notice" },
                { n: "2nd", label: "Final Written Warning" },
                { n: "3rd", label: "Termination Review" },
              ].map((w) => (
                <div key={w.n} style={styles.warningCard}>
                  <span style={styles.warningNum}>{w.n}</span>
                  <span style={styles.warningLabel}>{w.label}</span>
                </div>
              ))}
            </div>
            <p style={styles.subLabel}>Violations include:</p>
            <BulletList items={[
              "Repeated absence or lateness",
              "Misconduct or unprofessional behaviour",
              "Failure to perform assigned duties",
              "Ignoring company instructions",
            ]} />
          </SubSection>

          <SubSection title="1.4  Behaviour & Conduct">
            <BulletList items={[
              "Maintain professional communication with staff and clients at all times.",
              "Disrespectful, abusive, or unethical behaviour is strictly prohibited.",
              "Any misconduct may result in immediate disciplinary action.",
            ]} />
          </SubSection>

          <SubSection title="1.5  Performance Monitoring">
            <BulletList items={[
              "All employees are evaluated weekly based on performance metrics.",
              "Failure to meet expectations may result in training extension, role adjustment, or termination.",
            ]} />
          </SubSection>
        </Section>

        <div style={styles.divider} />

        {/* ══════════════════════════════════════
            SECTION 2 — SDR
        ══════════════════════════════════════ */}
        <Section number="02" title="Sales Development Representative (SDR)">

          <SubSection title="2.1  Role Overview">
            <BulletList items={[
              "Lead generation",
              "Cold calling and outreach",
              "Setting appointments",
              "Managing communication with potential clients",
              "Meeting weekly and monthly appointment targets",
            ]} />
          </SubSection>

          <SubSection title="2.2  Categories & Compensation">
            <div style={styles.categoryBlock}>
              <p style={styles.categoryTitle}>A)  Fresh SDR — No Experience</p>
              <BulletList items={[
                "Entry-level position — performance-based compensation",
                "Requires training and close supervision",
                "Salary strictly linked to performance metrics (details shared separately)",
              ]} />
            </div>
            <div style={styles.categoryBlock}>
              <p style={styles.categoryTitle}>B)  Experienced SDR</p>
              <BulletList items={[
                "Must have prior SDR or sales experience",
                "Must demonstrate strong English communication skills",
                "Eligible for higher package: base salary + performance bonus",
                "Final package shared in offer documentation",
              ]} />
            </div>
          </SubSection>

          <SubSection title="2.3  Probation & Test Period — 30 Days">
            <BulletList items={[
              "Week 1:  Minimum 15 confirmed appointments",
              "Week 2–4:  Progressive increase in targets based on performance",
              "Monthly minimum:  50 confirmed appointments required for continuation",
            ]} />
            <p style={styles.subLabel}>Outcomes:</p>
            <div style={{ ...styles.outcomeGrid }} className="outcome-grid">
              {[
                { icon: "✔", color: "#22c55e", text: "Meeting targets → eligible for long-term role + base salary" },
                { icon: "⚠", color: "#f59e0b", text: "Partial performance → extended probation or retraining" },
                { icon: "✘", color: "#ef4444", text: "Failure to perform → contract termination" },
              ].map((o) => (
                <div key={o.icon} style={styles.outcomeCard}>
                  <span style={{ ...styles.outcomeIcon, color: o.color }}>{o.icon}</span>
                  <span style={styles.outcomeText}>{o.text}</span>
                </div>
              ))}
            </div>
          </SubSection>

          <SubSection title="2.4  Meetings & Training Schedule">
            <BulletList items={[
              "3 sessions per week:  Monday, Wednesday, Friday",
              "Duration:  1 hour per session",
              "Focus:  Sales training, script improvement, performance review, objection handling",
            ]} />
          </SubSection>

          <SubSection title="2.5  Discipline & Termination">
            <p style={styles.subLabel}>Grounds for warning:</p>
            <BulletList items={[
              "Absenteeism or missing meetings",
              "Late reporting without prior notice",
              "Failure to meet daily or weekly targets",
              "Misconduct or unprofessional communication",
            ]} />
            <p style={styles.subLabel}>Immediate termination may occur in cases of:</p>
            <BulletList items={[
              "Consistent failure to meet performance targets",
              "Repeated absence without approval",
              "Serious misconduct or dishonesty",
              "Failure after completion of the probation period",
            ]} />
          </SubSection>

          {/* ── PDF BUTTONS ── */}
          <div style={styles.pdfButtonsRow} className="pdf-btn-row">
            <a
              href="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/docs/Unexperienced%20SDR.pdf"
              target="_blank"
              rel="noreferrer"
              className="pdf-btn-link pdf-btn-blue"
            >
              📋 Fresh SDR Guide
            </a>
            <a
              href="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/docs/Experienced%20SDR.pdf"
              target="_blank"
              rel="noreferrer"
              className="pdf-btn-link pdf-btn-purple"
            >
              📑 Experienced SDR Guide
            </a>
          </div>

        </Section>

        <div style={styles.divider} />

        {/* ══════════════════════════════════════
            SECTION 3 — TECHNICAL ASSISTANT
        ══════════════════════════════════════ */}
        <Section number="03" title="Technical Operations Assistant">

          <div style={styles.metaRow}>
            {[
              ["Works With", "Directly with Founder"],
              ["Hours", "11:00 AM – 4:00 PM"],
              ["Coding", "Not Required"],
              ["Evaluation", "Weekly"],
            ].map(([k, v]) => (
              <div key={k} style={styles.metaItem}>
                <span style={styles.metaKey}>{k}</span>
                <span style={styles.metaVal}>{v}</span>
              </div>
            ))}
          </div>

          <SubSection title="3.1  Role Overview">
            <BulletList items={[
              "Supporting technical and daily operations",
              "Handling CRM and outreach tools (e.g. GoHighLevel)",
              "Managing task systems (e.g. Asana)",
              "Updating websites and landing pages",
              "Creating banners and basic design assets",
              "Supporting outreach campaigns — email, CRM updates, tracking",
              "Operating all systems while the founder directs strategy",
            ]} />
          </SubSection>

          <SubSection title="3.2  Training Structure">
            <div style={styles.categoryBlock}>
              <p style={styles.categoryTitle}>Week 1 — Training Phase</p>
              <BulletList items={[
                "Full training under senior staff",
                "Learning company systems and processes",
                "No independent task responsibility during this phase",
              ]} />
            </div>
            <div style={styles.categoryBlock}>
              <p style={styles.categoryTitle}>Week 2 Onwards — Working Phase</p>
              <BulletList items={[
                "Begins active task execution",
                "Works under supervision with increasing independence",
                "Weekly performance review begins",
              ]} />
            </div>
          </SubSection>

          <SubSection title="3.3  Employment & Evaluation">
            <BulletList items={[
              "Employment is initially weekly performance-based",
              "Payment is processed weekly",
              "Continuation depends on performance review each week",
            ]} />
            <p style={styles.subLabel}>After 4 successful weeks, employee may be:</p>
            <BulletList items={[
              "Converted to a long-term role",
              "Offered a base salary structure",
              "Or continued on weekly contract if required",
            ]} />
          </SubSection>

          <SubSection title="3.4  Performance Expectations">
            <BulletList items={[
              "Complete assigned tasks on time and accurately",
              "Follow all instructions carefully and precisely",
              "Show continuous improvement throughout the training period",
              "Remain highly responsive during working hours",
            ]} />
          </SubSection>

          <SubSection title="3.5  Technical Expectations">
            <p style={styles.noteText}>
              No coding required. You must be comfortable using tools and following precise instructions.
            </p>
            <p style={styles.subLabel}>Tools you will use:</p>
            <BulletList items={[
              "Visual Studio Code (basic use, guided)",
              "CRM tools — GoHighLevel",
              "Website editors and landing page builders",
              "Outreach and tracking platforms",
            ]} />
          </SubSection>

          <SubSection title="3.6  Discipline & Termination">
            <p style={styles.subLabel}>Grounds for warning:</p>
            <BulletList items={[
              "Absence or lateness without notice",
              "Poor task completion or accuracy",
              "Misconduct or negligence",
              "Lack of cooperation with senior staff",
            ]} />
            <p style={styles.subLabel}>Termination conditions:</p>
            <BulletList items={[
              "Failure to improve after the training period",
              "Repeated weekly underperformance",
              "Misconduct or violation of company rules",
              "Failure to follow instructions from senior staff",
            ]} />
          </SubSection>
        </Section>

        <div style={styles.divider} />

        {/* ══════════════════════════════════════
            SECTION 4 — GENERAL NOTES
        ══════════════════════════════════════ */}
        <Section number="04" title="Important Notes — Both Roles">

          <SubSection title="4.1  Performance-Based Culture">
            <BulletList items={[
              "The company operates on a performance-driven model.",
              "Consistent output and discipline are mandatory for continuation.",
            ]} />
          </SubSection>

          <SubSection title="4.2  Growth Opportunity">
            <p style={styles.subLabel}>High-performing employees will be considered for:</p>
            <BulletList items={[
              "Salary increase",
              "Permanent employment",
              "Leadership roles within the company",
            ]} />
          </SubSection>

          <SubSection title="4.3  Communication Requirement">
            <BulletList items={[
              "All employees must remain reachable during working hours.",
              "Any absence or delay must be communicated in advance.",
            ]} />
          </SubSection>

          <SubSection title="4.4  Professional Standards">
            <BulletList items={[
              "Respectful communication is mandatory at all times.",
              "No harassment, abuse, or inappropriate behaviour is tolerated.",
            ]} />
          </SubSection>
        </Section>

        <div style={styles.divider} />

        {/* ── CLOSING NOTE ── */}
        <div style={styles.closingBox}>
          <p style={styles.closingTitle}>Final Note</p>
          <p style={styles.closingText}>
            This policy is designed to ensure productivity, fair performance evaluation,
            structured growth opportunities, and a professional work environment.
            All employees are expected to uphold these standards from their first day.
          </p>
          <Link to="/hiring/apply" style={styles.applyBtn}>
          Apply Now →
          </Link>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    background: "#0d1117",
    color: "#fff",
    fontFamily: "elza, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    minHeight: "100vh",
    overflowX: "hidden",
    width: "100%",
  },

  hero: {
    background: "linear-gradient(160deg, #0a0d14 0%, #121723 60%, #1a0a2e 100%)",
    padding: "140px 20px 80px",
    textAlign: "center",
    borderBottom: "1px solid rgba(123,25,188,0.2)",
  },
  heroLabel: {
    fontSize: "12px",
    color: "#9f3ee5",
    textTransform: "uppercase",
    letterSpacing: "3px",
    marginBottom: "16px",
  },
  heroTitle: {
    fontSize: "52px",
    fontWeight: 500,
    margin: "0 0 14px",
    color: "#fff",
    wordBreak: "break-word",
    lineHeight: 1.1,
  },
  heroSub: {
    fontSize: "18px",
    color: "#a0a6b5",
    marginBottom: "28px",
  },
  heroPills: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    justifyContent: "center",
  },
  pill: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "999px",
    padding: "5px 14px",
    fontSize: "12px",
    color: "#d1d5db",
  },

  inner: {
    maxWidth: "860px",
    margin: "0 auto",
    padding: "60px 20px 80px",
    width: "100%",
    boxSizing: "border-box",
  },

  rolesGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    marginBottom: "0",
  },
  roleCard: {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "14px",
    padding: "24px",
    cursor: "pointer",
    boxSizing: "border-box",
    transition: "border-color 0.25s ease",
  },
  roleCardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "14px",
    gap: "12px",
  },
  roleCardLabel: {
    fontSize: "11px",
    color: "#7b19bc",
    textTransform: "uppercase",
    letterSpacing: "2px",
    marginBottom: "6px",
  },
  roleCardTitle: {
    fontSize: "17px",
    fontWeight: 600,
    color: "#fff",
    margin: "0 0 6px",
    lineHeight: 1.3,
  },
  roleCardSub: {
    fontSize: "13px",
    color: "#6b7280",
    margin: 0,
  },
  chevron: {
    color: "#7b19bc",
    fontSize: "12px",
    flexShrink: 0,
    marginTop: "2px",
  },
  roleTagRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  tag: {
    background: "rgba(123,25,188,0.12)",
    border: "1px solid rgba(123,25,188,0.25)",
    borderRadius: "999px",
    padding: "3px 10px",
    fontSize: "11px",
    color: "#c084fc",
  },

  divider: {
    borderTop: "1px solid rgba(255,255,255,0.07)",
    margin: "50px 0",
  },

  section: { marginBottom: "0" },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "28px",
  },
  sectionNumber: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#7b19bc",
    letterSpacing: "2px",
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: "22px",
    fontWeight: 600,
    color: "#fff",
    margin: 0,
    wordBreak: "break-word",
  },
  sectionBody: { paddingLeft: "0" },

  introText: {
    fontSize: "15px",
    color: "#9ca3af",
    lineHeight: 1.7,
    marginBottom: "28px",
    fontStyle: "italic",
  },

  subSection: { marginBottom: "28px" },
  subTitle: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#e2e8f0",
    margin: "0 0 10px",
    paddingLeft: "0",
  },

  list: { listStyle: "none", padding: 0, margin: 0 },
  listItem: {
    display: "flex",
    gap: "12px",
    fontSize: "15px",
    color: "#9ca3af",
    lineHeight: 1.65,
    marginBottom: "6px",
    alignItems: "flex-start",
  },
  bullet: { color: "#7b19bc", flexShrink: 0, marginTop: "1px" },

  subLabel: {
    fontSize: "13px",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "1px",
    margin: "16px 0 8px",
  },

  warningGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "12px",
    marginBottom: "20px",
  },
  warningCard: {
    background: "rgba(123,25,188,0.07)",
    border: "1px solid rgba(123,25,188,0.2)",
    borderRadius: "10px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  warningNum: {
    fontSize: "11px",
    color: "#9f3ee5",
    textTransform: "uppercase",
    letterSpacing: "1px",
    fontWeight: 600,
  },
  warningLabel: {
    fontSize: "14px",
    color: "#e2e8f0",
    lineHeight: 1.4,
  },

  outcomeGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "12px",
    marginTop: "10px",
  },
  outcomeCard: {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: "10px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  outcomeIcon: { fontSize: "18px" },
  outcomeText: { fontSize: "13px", color: "#9ca3af", lineHeight: 1.5 },

  categoryBlock: {
    marginBottom: "18px",
    paddingLeft: "14px",
    borderLeft: "2px solid rgba(123,25,188,0.3)",
  },
  categoryTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#c084fc",
    margin: "0 0 8px",
  },

  // PDF buttons
  pdfButtonsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "14px",
    marginTop: "32px",
  },

  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "28px",
  },
  metaItem: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "8px",
    padding: "10px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  metaKey: {
    fontSize: "10px",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  metaVal: {
    fontSize: "14px",
    color: "#e2e8f0",
    fontWeight: 500,
  },

  noteText: {
    fontSize: "14px",
    color: "#9ca3af",
    fontStyle: "italic",
    marginBottom: "12px",
    lineHeight: 1.6,
  },

  closingBox: {
    background: "rgba(123,25,188,0.06)",
    border: "1px solid rgba(123,25,188,0.2)",
    borderRadius: "16px",
    padding: "40px",
    textAlign: "center",
  },
  closingTitle: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#fff",
    marginBottom: "12px",
  },
  closingText: {
    fontSize: "15px",
    color: "#9ca3af",
    lineHeight: 1.8,
    maxWidth: "560px",
    margin: "0 auto 28px",
  },
  applyBtn: {
    display: "inline-block",
    background: "linear-gradient(135deg, #7b19bc, #9f3ee5)",
    color: "#fff",
    padding: "14px 40px",
    fontSize: "15px",
    fontWeight: 600,
    textDecoration: "none",
    borderRadius: "4px",
    fontFamily: "elza, sans-serif",
  },
};